/**
 * Reading a program's model out of GitLab.
 *
 * The branch list is the repository's answer, not BEST's, so a branch that
 * cannot be pushed to says so here rather than failing at commit time in a
 * later phase.
 *
 * Importing REPLACES the working database for that branch. That is stated
 * plainly rather than discovered: a program's database is a working copy of
 * one ref, and anything edited in BEST but not yet written back to the .sysml
 * is lost.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { BranchRow, ImportResult } from '../../schema'
import { useProject } from '../../state/Project'

export default function ImportDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { current, reload } = useProject()
  const slug = current?.slug ?? ''

  const [branches, setBranches] = useState<BranchRow[] | null>(null)
  const [ref, setRef] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [failures, setFailures] = useState<Array<{ path: string; detail: string }>>([])
  const [done, setDone] = useState<ImportResult | null>(null)

  useEffect(() => {
    if (!open || !slug) return
    setBranches(null)
    setError('')
    setFailures([])
    setDone(null)
    api
      .branches(slug)
      .then((payload) => {
        setBranches(payload.items)
        const fallback =
          payload.items.find((b) => b.name === current?.default_branch) ??
          payload.items.find((b) => b.is_default)
        setRef(fallback?.name ?? payload.items[0]?.name ?? '')
      })
      .catch((failure: ApiError) => setError(failure.message))
  }, [open, slug, current?.default_branch])

  const run = async () => {
    setBusy(true)
    setError('')
    setFailures([])
    try {
      const result = await api.importProject(slug, ref)
      setDone(result)
      // The model just changed underneath every screen.
      await reload()
    } catch (failure) {
      if (failure instanceof ApiError) {
        setError(failure.message)
        // A 422 names which files would not parse, keyed by path. Nothing was
        // written, so showing them is a to-do list rather than a post-mortem.
        setFailures(
          Object.entries(failure.fields).map(([path, detail]) => ({ path, detail })),
        )
      } else {
        setError('The import failed.')
      }
    } finally {
      setBusy(false)
    }
  }

  const chosen = branches?.find((b) => b.name === ref)

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import from GitLab</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            <AlertTitle>Imported {done.ref}</AlertTitle>
            {done.requirements} requirements and {done.verification} verification
            elements from {done.files} file{done.files === 1 ? '' : 's'}, at{' '}
            <Box component="code">{done.sha.slice(0, 8)}</Box>.
          </Alert>
        ) : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              Replaces this program&rsquo;s working database with what the
              repository holds. Anything changed in BEST and not yet written back
              to the .sysml is lost.
            </DialogContentText>

            {branches === null && !error ? (
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', py: 2 }}>
                <CircularProgress size={20} />
                <Typography color="text.secondary">Reading the branches…</Typography>
              </Stack>
            ) : null}

            {branches ? (
              <TextField
                select
                label="Branch"
                value={ref}
                onChange={(event) => setRef(event.target.value)}
                fullWidth
                sx={{ mt: 1 }}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.name} value={branch.name}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', width: '100%' }}
                    >
                      <span>{branch.name}</span>
                      {branch.is_default ? <Chip label="default" size="small" /> : null}
                      {branch.is_protected ? (
                        <Chip label="protected" size="small" variant="outlined" />
                      ) : null}
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            ) : null}

            {chosen && !chosen.can_push ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                You cannot push to {chosen.name}, so BEST will read it but not
                write to it.
              </Alert>
            ) : null}
          </>
        )}

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Nothing was imported</AlertTitle>
            {error}
          </Alert>
        ) : null}

        {failures.map((problem) => (
          <Box
            key={problem.path}
            component="pre"
            sx={{ mt: 1, overflowX: 'auto', fontSize: '0.8rem' }}
          >
            {problem.detail}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {done ? 'Close' : 'Cancel'}
        </Button>
        {done ? null : (
          <Button
            onClick={() => void run()}
            variant="contained"
            disabled={busy || !ref}
          >
            {busy ? 'Importing…' : 'Import'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
